import { create } from 'zustand';
import {
  apiClient,
  ScheduledQueryCreateBody,
  ScheduledQueryResponse,
  ScheduledQueryResultResponse,
  ScheduledQueryUpdateBody,
} from '@/utils/apiClient';

interface ScheduledQueriesState {
  queries: ScheduledQueryResponse[];
  loading: boolean;
  error: string | null;
  selectedQueryHistory: ScheduledQueryResultResponse[];
  historyLoading: boolean;
}

interface ScheduledQueriesActions {
  fetchQueries: () => Promise<void>;
  createQuery: (body: ScheduledQueryCreateBody) => Promise<ScheduledQueryResponse>;
  updateQuery: (id: number, body: ScheduledQueryUpdateBody) => Promise<void>;
  removeQuery: (id: number) => Promise<void>;
  runNow: (id: number) => Promise<ScheduledQueryResultResponse>;
  fetchHistory: (id: number) => Promise<void>;
  clearError: () => void;
}

interface ScheduledQueriesStore extends ScheduledQueriesState, ScheduledQueriesActions {}

export const useScheduledQueriesStore = create<ScheduledQueriesStore>((set, get) => ({
  // State
  queries: [],
  loading: false,
  error: null,
  selectedQueryHistory: [],
  historyLoading: false,

  // Actions
  fetchQueries: async () => {
    set({ loading: true, error: null });
    try {
      const queries = await apiClient.listScheduledQueries();
      set({ queries, loading: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load scheduled queries';
      set({ error: message, loading: false });
    }
  },

  createQuery: async (body) => {
    set({ error: null });
    try {
      const created = await apiClient.createScheduledQuery(body);
      set((state) => ({ queries: [created, ...state.queries] }));
      return created;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create query';
      set({ error: message });
      throw err;
    }
  },

  updateQuery: async (id, body) => {
    set({ error: null });
    try {
      const updated = await apiClient.updateScheduledQuery(id, body);
      set((state) => ({
        queries: state.queries.map((q) => (q.id === id ? updated : q)),
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to update query';
      set({ error: message });
      throw err;
    }
  },

  removeQuery: async (id) => {
    set({ error: null });
    try {
      await apiClient.deleteScheduledQuery(id);
      set((state) => ({
        queries: state.queries.filter((q) => q.id !== id),
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to remove query';
      set({ error: message });
      throw err;
    }
  },

  runNow: async (id) => {
    set({ error: null });
    try {
      // Mark as running in local state
      set((state) => ({
        queries: state.queries.map((q) =>
          q.id === id ? { ...q, last_status: 'running' } : q
        ),
      }));
      const result = await apiClient.runScheduledQueryNow(id);
      // Refresh the query to get updated last_run_at etc.
      try {
        const updated = await apiClient.getScheduledQuery(id);
        set((state) => ({
          queries: state.queries.map((q) => (q.id === id ? updated : q)),
        }));
      } catch {
        // best-effort refresh
      }
      return result;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to run query';
      set({ error: message });
      // Revert running status
      await get().fetchQueries();
      throw err;
    }
  },

  fetchHistory: async (id) => {
    set({ historyLoading: true });
    try {
      const history = await apiClient.getScheduledQueryHistory(id);
      set({ selectedQueryHistory: history, historyLoading: false });
    } catch (err: unknown) {
      set({ selectedQueryHistory: [], historyLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
